import React, { PureComponent, useMemo } from 'react';
import styled, { css } from 'styled-components';

import AntdTable from 'antd/lib/table';
import AntdInput from 'antd/lib/input';
import AmtdCheckbox from 'antd/lib/checkbox';
import AntdButton from 'antd/lib/button';
import AntdPopover from 'antd/lib/popover';
import {
  PlusOutlined,
  DeleteOutlined,
  BarsOutlined,
} from '@ant-design/icons';

import Admin from 'hive-admin/src/components/Admin';
import Field from 'hive-admin/src/components/Field';

export const InputWrapper = styled.div`
  ${props => props.minWidth && css`min-width: ${props.minWidth};`}
  ${props => props.maxWidth && css`max-width: ${props.maxWidth};`}
  width: 100%;
`;

export const InputAligned = styled(AntdInput)`
  text-align: ${({ align }) => align || 'left'};
  width: 100%;
`;

export const InputCheckbox = ({ value, ...props }) => (
  <AmtdCheckbox
    {...props}
    checked={!!value}
    data-input-value-uses-checked="true"
  />
);

export const InputFormField = ({ fieldName, fieldConfig, ...props }) => {
  const field = useMemo(
    () => Admin.compileFromLibrary([fieldName, fieldConfig]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  return field.render({ ...props });
};

const LabelWrapper = styled.div`
  white-space: nowrap;
`;

const ColumnControlsPopoverWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

export class ColumnControls extends PureComponent {
  constructor(props) {
    super(props);
    this.ref = React.createRef();
  }

  renderAction(name, icon, onClick) {
    return (
      <AntdButton
        type="link"
        onClick={() => {
          this.ref.current.tooltip.setState({ visible: false });
          onClick();
        }}
      >
        {icon}
        &nbsp;
        {name}
      </AntdButton>
    );
  }

  render() {
    return (
      <AntdPopover
        ref={this.ref}
        title={null}
        trigger={this.props.disabled ? [] : 'click'}
        placement="right"
        content={(
          <ColumnControlsPopoverWrapper>
            {this.renderAction(
              'Above',
              <PlusOutlined />,
              () => this.props.onAdd(this.props.rowIndex, 0),
            )}
            {this.renderAction(
              'Below',
              <PlusOutlined />,
              () => this.props.onAdd(this.props.rowIndex, 1),
            )}
            {this.renderAction(
              'Remove',
              <DeleteOutlined />,
              () => this.props.onRemove(this.props.rowIndex),
            )}
          </ColumnControlsPopoverWrapper>
        )}
      >
        <BarsOutlined />
      </AntdPopover>
    );
  }
}

export class ColumnValue extends PureComponent {
  static getFieldProps({ props = {} }) {
    return props;
  }

  static getColumnLabel({ label }) {
    return label;
  }

  render() {
    const {
      fields,
      render,
      value = {},
      rowIndex,
      onChange,
      getLabel = this.constructor.getColumnLabel,
    } = this.props;
    return render
    ? render(value, this.props)
    : fields
    ? fields.map((field) => {
        const {
          name,
          maxWidth,
          minWidth,
          ComponentWrapper = InputWrapper,
          ComponentInput = InputAligned,
          getProps = this.constructor.getFieldProps,
          render: renderField,
        } = field;
        const inputProps = {
          value: value[name],
          'data-input-row': rowIndex,
          'data-input-name': name,
          onChange,
          disabled: this.props.disabled,
        };
        Object.assign(
          inputProps,
          getProps(field, value, this.props, inputProps),
        );
        return (
          <ComponentWrapper key={name} maxWidth={maxWidth} minWidth={minWidth}>
            {
                renderField
              ? renderField(inputProps, this.props)
              : (
                  <ComponentInput
                    {...inputProps}
                  />
                )
            }
          </ComponentWrapper>
        );
      })
    : (
        <LabelWrapper>
          {getLabel(value, this.props)}
        </LabelWrapper>
      );
  }
}

export class Table extends PureComponent {
  handleRowAdd = (rowIndex, relativeRowIndex = 1) => {
    this.props.onChange(this.props.value.reduce(
      (agr, row, index) => {
        if (index === rowIndex && relativeRowIndex === 0) {
          agr.push(this.props.controlsGetNewItem(
            this.rowIndex + relativeRowIndex,
            row,
            this.props,
          ));
        }
        agr.push(row);
        if (index === rowIndex && relativeRowIndex === 1) {
          agr.push(this.props.controlsGetNewItem(
            this.rowIndex + relativeRowIndex,
            row,
            this.props,
          ));
        }
        return agr;
      },
      [],
    ));
  }

  handleRowRemove = (rowIndex) => {
    this.props.onChange(this.props.value.reduce(
      (agr, row, index) => {
        if (index !== rowIndex) {
          agr.push(row);
        }
        return agr;
      },
      [],
    ));
  }

  handleChange = (event) => {
    const { nativeEvent, target } = event;
    const { value, checked } = target;
    const {
      inputName,
      inputRow: inputRowString,
      inputValueUsesChecked,
    } = (nativeEvent.target.dataset || {});
    if (inputName) {
      const inputRow = parseInt(inputRowString, 10);
      this.props.onChange(this.props.value.map((row, index) => {
        if (index === inputRow) {
          return {
            ...row,
            [inputName]: inputValueUsesChecked ? checked : value,
          };
        }
        return row;
      }));
    }
  }

  handleFormChange = (rowIndex, name, value) => {
    this.props.onChange(this.props.value.map((row, index) => {
      if (index === rowIndex) {
        return {
          ...row,
          [name]: value,
        };
      }
      return row;
    }));
  }

  renderColumnControls = (text, row, rowIndex) => (
    <ColumnControls
      rowIndex={rowIndex}
      onAdd={this.handleRowAdd}
      onRemove={this.handleRowRemove}
      disabled={this.props.disabled}
    />
  )

  renderColumnValue(
    text,
    row = {},
    rowIndex,
    columnIndex,
    render,
    fields,
    label,
    getLabel,
  ) {
    return (
      <ColumnValue
        value={row}
        rowIndex={rowIndex}
        columnIndex={columnIndex}
        render={render}
        fields={fields}
        label={label}
        getLabel={getLabel}
        onChange={this.handleChange}
        onFormChange={this.handleFormChange}
        disabled={this.props.disabled}
        form={this.props.form}
        fieldProps={this.props.fieldProps}
      />
    );
  }

  render() {
    const {
      columns = [],
      value,
      controlsEnabled,
      controlsGetNewItem,
      table = {},
      ...props
    } = this.props;
    const controlColumn = controlsEnabled
    ? (
        <AntdTable.Column
          key="control-column-add-remove"
          align="center"
          render={this.renderColumnControls}
          width="30px"
        />
      )
    : null;
    return (
      <AntdTable
        dataSource={value}
        size="middle"
        rowKey="_id"
        showHeader={false}
        pagination={false}
        scroll={{ x: 'max-content' }}
        bordered
        {...props}
        {...table}
      >
        {controlColumn}
        {columns.map(({
          key,
          fields,
          label,
          getLabel,
          render,
          ...column
        }, index) => (
          <AntdTable.Column
            key={key || index}
            {...column}
            render={(...args) => this.renderColumnValue(
              ...args,
              index,
              render,
              fields,
              label,
              getLabel,
            )}
          />
        ))}
      </AntdTable>
    );
  }
}

export default class FieldTable extends Field {
  static config = {
    ...Field.config,
    controlsEnabled: false,
    controlsGetNewItem: (/* index, props */) => ({}),
    getColumns: (props) => props.columns,
  }

  renderInput(props) {
    return (
      <Table
        {...props}
        columns={this.props.getColumns(this.props)}
        controlsEnabled={this.props.controlsEnabled}
        controlsGetNewItem={this.props.controlsGetNewItem}
        {...(this.props.table || {})}
        form={this.props.form}
        fieldProps={this.props}
      />
    );
  }
}

Admin.addToLibrary(
  'FieldTable',
  config => FieldTable.create(config),
);
